import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { fetchUserByUniqueId } from "../../redux/actions/userActions";
import {
  deleteLoginCookieAndSession,
  getLoginCookieAndSession,
} from "../../utils";
import { USER_JUST_LOGGED_IN } from "../../constants";

import { CenteredLoading } from "../../components/common/CenteredLoading";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { SEO } from "../../components/common/SEO";
import { SnackbarNotify } from "../../components/common/SnackbarNotify";
import { FormActionButton } from "../../components/common/FormActionButton";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

const BRAND_ORANGE = "#e78758";
const BRAND_ORANGE_DARK = "#cf7045";
const PAGE_BACKGROUND = "#f5f7f9";
const COMMISSION_RATE = "9.5%";

const Presell: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const finalCtaRef = useRef<HTMLDivElement | null>(null);

  const { loading, users } = useSelector((state: RootState) => state.users);

  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);

  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleFaqChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedFaq(isExpanded ? panel : false);
    };

  useEffect(() => {
    const profileId = getLoginCookieAndSession();

    if (profileId) {
      dispatch(fetchUserByUniqueId(Number(profileId)));
    } else {
      deleteLoginCookieAndSession();
      navigate("/");
    }
  }, [dispatch, navigate]);

  if (!loading && users && users.length <= 0) {
    deleteLoginCookieAndSession();
    navigate("/");
    return null;
  }

  const handleStartSetup = () => {
    navigate("/seller-setup");
  };

  const handleBrowseInstead = () => {
    navigate("/latest-toys");
  };

  const handleScrollToSetup = () => {
    finalCtaRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  if (loading || !users) {
    return <CenteredLoading />;
  }

  return (
    <>
      <SEO
        title="Start Selling • FiddlePiddle • Toys Marketplace"
        description="Set up your secure FiddlePiddle seller account, list toys for sale, receive secure Stripe payouts, and manage your sales in one place."
        keywordsContent="sell toys online, toy marketplace, FiddlePiddle seller setup, Stripe payouts, sell collectibles, sell used toys"
      />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: PAGE_BACKGROUND,
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 100,
            bgcolor: "#ffffff",
          }}
        >
          <Breadcrumb
            items={[
              { text: "Home", url: "/" },
              { text: "Start Selling", url: "" },
            ]}
          />
        </Box>

        <Container
          maxWidth="lg"
          sx={{
            pt: {
              xs: 2,
              sm: 3,
              md: 4,
            },
            pb: {
              xs: 5,
              md: 7,
            },
          }}
        >
          <SnackbarNotify
            message="Signed in successfully"
            sessionKey={USER_JUST_LOGGED_IN}
          />

          {/* Main seller setup hero */}
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              mb: 3,
              borderRadius: {
                xs: "22px",
                md: "30px",
              },
              color: "#ffffff",
              background:
                "linear-gradient(135deg, #1b252d 0%, #283943 56%, #354d5a 100%)",
              boxShadow: "0 24px 60px rgba(24, 34, 42, 0.22)",

              "&::before": {
                content: '""',
                position: "absolute",
                width: {
                  xs: 320,
                  md: 520,
                },
                height: {
                  xs: 320,
                  md: 520,
                },
                top: {
                  xs: -180,
                  md: -280,
                },
                right: {
                  xs: -170,
                  md: -170,
                },
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(231,135,88,0.68) 0%, rgba(231,135,88,0) 70%)",
                pointerEvents: "none",
              },

              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                opacity: 0.18,
                backgroundImage: `
                  linear-gradient(
                    rgba(255,255,255,0.08) 1px,
                    transparent 1px
                  ),
                  linear-gradient(
                    90deg,
                    rgba(255,255,255,0.08) 1px,
                    transparent 1px
                  )
                `,
                backgroundSize: "50px 50px",
                maskImage:
                  "linear-gradient(to right, rgba(0,0,0,0.85), transparent)",
                pointerEvents: "none",
              },
            }}
          >
            <Grid
              container
              spacing={{
                xs: 3,
                md: 5,
              }}
              alignItems="center"
              sx={{
                position: "relative",
                zIndex: 1,
                p: {
                  xs: 2.5,
                  sm: 3.5,
                  md: 5,
                },
              }}
            >
              <Grid item xs={12} md={7}>
                <Chip
                  size="small"
                  icon={<RocketLaunchRoundedIcon />}
                  label="ONE-TIME SELLER SETUP"
                  sx={{
                    mb: 2,
                    color: "#ffffff",
                    bgcolor: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.16)",
                    fontSize: "0.72rem",
                    fontWeight: 850,
                    letterSpacing: "0.06em",

                    "& .MuiChip-icon": {
                      color: "#ffad82",
                    },
                  }}
                />

                <Typography
                  component="h1"
                  sx={{
                    maxWidth: 680,
                    mb: 2,
                    fontSize: {
                      xs: "2.25rem",
                      sm: "3.05rem",
                      md: "3.65rem",
                    },
                    lineHeight: {
                      xs: 1.08,
                      md: 1.04,
                    },
                    fontWeight: 900,
                    letterSpacing: "-0.045em",
                  }}
                >
                  Turn your unused toys into something valuable.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    maxWidth: 650,
                    mb: 2.75,
                    color: "rgba(255,255,255,0.76)",
                    fontSize: {
                      xs: "1rem",
                      sm: "1.1rem",
                    },
                    lineHeight: 1.7,
                  }}
                >
                  Complete a quick seller setup, securely connect your payout
                  information, and start listing toys for buyers across
                  FiddlePiddle.
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: {
                      xs: "center",
                      sm: "flex-start",
                    },
                    gap: 1,
                    mb: 2.75,
                  }}
                >
                  <Chip
                    size="small"
                    icon={<CheckCircleOutlineIcon />}
                    label="Free seller setup"
                    sx={{
                      color: "#ffffff",
                      bgcolor: "rgba(82,195,137,0.13)",
                      border: "1px solid rgba(116,225,167,0.2)",

                      "& .MuiChip-icon": {
                        color: "#79e0aa",
                      },
                    }}
                  />

                  <Chip
                    size="small"
                    icon={<SellRoundedIcon />}
                    label={`${COMMISSION_RATE} when an item sells`}
                    sx={{
                      color: "#ffffff",
                      bgcolor: "rgba(255,255,255,0.09)",
                      border: "1px solid rgba(255,255,255,0.14)",

                      "& .MuiChip-icon": {
                        color: "#ffad82",
                      },
                    }}
                  />

                  <Chip
                    size="small"
                    icon={<PaymentsOutlinedIcon />}
                    label="Secure Stripe payouts"
                    sx={{
                      color: "#ffffff",
                      bgcolor: "rgba(255,255,255,0.09)",
                      border: "1px solid rgba(255,255,255,0.14)",

                      "& .MuiChip-icon": {
                        color: "#ffad82",
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: {
                      xs: "column",
                      sm: "row",
                    },
                    alignItems: {
                      xs: "stretch",
                      sm: "center",
                    },
                    gap: 1.25,
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardRoundedIcon />}
                    onClick={handleScrollToSetup}
                    sx={{
                      minHeight: 52,
                      width: {
                        xs: "100%",
                        sm: "auto",
                      },
                      px: 3.25,
                      borderRadius: "999px",
                      color: "#ffffff",
                      bgcolor: BRAND_ORANGE,
                      fontSize: "1rem",
                      fontWeight: 850,
                      textTransform: "none",
                      boxShadow: "0 14px 32px rgba(231,135,88,0.38)",
                      transition:
                        "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",

                      "&:hover": {
                        color: "#ffffff",
                        bgcolor: BRAND_ORANGE_DARK,
                        boxShadow: "0 18px 38px rgba(231,135,88,0.48)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    Get Started
                  </Button>

                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.62)",
                      textAlign: {
                        xs: "center",
                        sm: "left",
                      },
                    }}
                  >
                    Review and accept the Seller Terms below.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: {
                      xs: 2.25,
                      sm: 2.75,
                    },
                    borderRadius: "24px",
                    color: "#ffffff",
                    bgcolor: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.16)",
                    backdropFilter: "blur(16px)",
                    boxShadow: "0 20px 46px rgba(0,0,0,0.2)",
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      display: "block",
                      mb: 2,
                      color: "rgba(255,255,255,0.62)",
                      fontWeight: 850,
                      letterSpacing: "0.08em",
                    }}
                  >
                    Start selling in three steps
                  </Typography>

                  <Stack spacing={1.5}>
                    {[
                      {
                        number: "1",
                        title: "Confirm your details",
                        description:
                          "Provide the basic information Stripe needs to verify your seller account.",
                      },
                      {
                        number: "2",
                        title: "Connect your payouts",
                        description:
                          "Choose the bank account or eligible debit card where you want to get paid.",
                      },
                      {
                        number: "3",
                        title: "List your first toy",
                        description:
                          "Once approved, create your listing and reach buyers looking for it.",
                      },
                    ].map((step) => (
                      <Box
                        key={step.number}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: "17px",
                          bgcolor: "rgba(10,17,22,0.25)",
                          border: "1px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "11px",
                            color: "#ffffff",
                            bgcolor: BRAND_ORANGE,
                            fontSize: "0.88rem",
                            fontWeight: 900,
                            boxShadow: "0 8px 18px rgba(231,135,88,0.28)",
                          }}
                        >
                          {step.number}
                        </Box>

                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 0.25,
                              fontWeight: 800,
                            }}
                          >
                            {step.title}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              color: "rgba(255,255,255,0.62)",
                              lineHeight: 1.5,
                            }}
                          >
                            {step.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Trust cards */}
          <Grid container spacing={2.25} sx={{ mb: 4 }}>
            {[
              {
                icon: <LockOutlinedIcon />,
                title: "Secure seller payouts",
                description:
                  "Your payout details are handled securely through Stripe.",
              },
              {
                icon: <VerifiedUserOutlinedIcon />,
                title: "Verified seller profile",
                description:
                  "Verification helps buyers feel more confident purchasing from you.",
              },
              {
                icon: <BoltRoundedIcon />,
                title: "Simple ongoing selling",
                description:
                  "Complete setup once, then manage future listings and sales from your account.",
              },
            ].map((item) => (
              <Grid item xs={12} md={4} key={item.title}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: 2.5,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.75,
                    borderRadius: "20px",
                    bgcolor: "#ffffff",
                    border: "1px solid #e2e6ea",
                    boxShadow: "0 12px 32px rgba(35,45,55,0.06)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",

                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 18px 38px rgba(35,45,55,0.1)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "15px",
                      color: BRAND_ORANGE_DARK,
                      bgcolor: "rgba(231,135,88,0.12)",

                      "& .MuiSvgIcon-root": {
                        fontSize: "1.45rem",
                      },
                    }}
                  >
                    {item.icon}
                  </Box>

                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 0.5,
                        fontWeight: 850,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.55,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Setup explanation */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: {
                    xs: 2.5,
                    sm: 3,
                  },
                  borderRadius: "22px",
                  bgcolor: "#ffffff",
                  border: "1px solid #e2e6ea",
                  boxShadow: "0 12px 32px rgba(35,45,55,0.05)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "14px",
                      color: BRAND_ORANGE_DARK,
                      bgcolor: "rgba(231,135,88,0.12)",
                    }}
                  >
                    <StorefrontRoundedIcon />
                  </Box>

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 850,
                        lineHeight: 1.25,
                      }}
                    >
                      How seller setup works
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      A quick, guided process powered by Stripe.
                    </Typography>
                  </Box>
                </Box>

                <Stack spacing={2}>
                  {[
                    "Stripe will guide you through a secure form for identity and payout verification.",
                    "You will provide basic personal details and choose where you want your payouts sent.",
                    "Stripe may request additional information or identification when required.",
                    "Once your account is approved, you can create listings and manage sales from your FiddlePiddle account.",
                  ].map((text) => (
                    <Box
                      key={text}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.25,
                      }}
                    >
                      <CheckCircleOutlineIcon
                        sx={{
                          mt: "2px",
                          flexShrink: 0,
                          fontSize: "1.2rem",
                          color: "#49a878",
                        }}
                      />

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.65,
                        }}
                      >
                        {text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: {
                    xs: 2.5,
                    sm: 3,
                  },
                  borderRadius: "22px",
                  color: "#ffffff",
                  background:
                    "linear-gradient(145deg, #263640 0%, #324955 100%)",
                  boxShadow: "0 16px 38px rgba(35,45,55,0.15)",
                }}
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "16px",
                    color: "#ffffff",
                    background:
                      "linear-gradient(135deg, #f1986c 0%, #dd7242 100%)",
                    boxShadow: "0 10px 24px rgba(221,114,66,0.32)",
                  }}
                >
                  <SecurityRoundedIcon />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    mb: 0.75,
                    fontWeight: 850,
                  }}
                >
                  Have these ready
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mb: 2.5,
                    color: "rgba(255,255,255,0.65)",
                    lineHeight: 1.6,
                  }}
                >
                  Preparing these items can help you complete setup without
                  interruption.
                </Typography>

                <Stack spacing={1.5}>
                  {[
                    "Your full legal name and residential address",
                    "A bank account or eligible debit card for payouts",
                    "A government-issued ID if Stripe requests additional verification",
                  ].map((requirement) => (
                    <Box
                      key={requirement}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                      }}
                    >
                      <CheckCircleOutlineIcon
                        sx={{
                          mt: "2px",
                          flexShrink: 0,
                          fontSize: "1.15rem",
                          color: "#79e0aa",
                        }}
                      />

                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255,255,255,0.82)",
                          lineHeight: 1.55,
                        }}
                      >
                        {requirement}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Shipping section */}
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              p: {
                xs: 2.5,
                sm: 3.25,
              },
              borderRadius: "24px",
              bgcolor: "#ffffff",
              border: "1px solid #e2e6ea",
              boxShadow: "0 14px 34px rgba(35,45,55,0.06)",
            }}
          >
            <Box
              sx={{
                maxWidth: 730,
                mb: 3,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: BRAND_ORANGE_DARK,
                  fontWeight: 850,
                  letterSpacing: "0.08em",
                }}
              >
                SELLER RESPONSIBILITIES
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mt: 0.25,
                  mb: 0.75,
                  fontWeight: 900,
                  letterSpacing: "-0.025em",
                }}
              >
                A smooth sale starts with reliable shipping.
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.65,
                }}
              >
                Buyers rely on accurate listings, timely shipping, and clear
                communication. These basic practices help keep the marketplace
                dependable.
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {[
                {
                  icon: <ScheduleOutlinedIcon />,
                  title: "Ship within 72 hours",
                  description:
                    "After receiving an order, package the item and hand it to the carrier within 72 hours.",
                },
                {
                  icon: <LocalShippingOutlinedIcon />,
                  title: "Add tracking promptly",
                  description:
                    "Enter the shipment tracking number from your account so the buyer can follow the delivery.",
                },
                {
                  icon: <EmailOutlinedIcon />,
                  title: "Keep buyers informed",
                  description:
                    "Contact the buyer when there is an unexpected delay, question, or important order update.",
                },
              ].map((item) => (
                <Grid item xs={12} md={4} key={item.title}>
                  <Box
                    sx={{
                      height: "100%",
                      p: 2.25,
                      borderRadius: "18px",
                      bgcolor: "#f7f8fa",
                      border: "1px solid #e7eaed",
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "13px",
                        color: BRAND_ORANGE_DARK,
                        bgcolor: "rgba(231,135,88,0.12)",
                      }}
                    >
                      {item.icon}
                    </Box>

                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 0.65,
                        fontWeight: 850,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Fees and payout cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: {
                    xs: 2.5,
                    sm: 3,
                  },
                  borderRadius: "22px",
                  bgcolor: "#ffffff",
                  border: "1px solid #e2e6ea",
                  boxShadow: "0 12px 30px rgba(35,45,55,0.05)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "15px",
                      color: "#3f996b",
                      bgcolor: "rgba(69,164,112,0.12)",
                    }}
                  >
                    <AccountBalanceWalletRoundedIcon />
                  </Box>

                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 0.75,
                        fontWeight: 850,
                      }}
                    >
                      Payout timeline
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.25,
                        lineHeight: 1.65,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: "text.primary",
                          fontWeight: 800,
                        }}
                      >
                        First payout:
                      </Box>{" "}
                      Usually held for approximately 7–14 days after your first
                      completed sale while Stripe completes its initial safety
                      review.
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.65,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: "text.primary",
                          fontWeight: 800,
                        }}
                      >
                        Future payouts:
                      </Box>{" "}
                      Generally processed on a rolling schedule about every two
                      days.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  position: "relative",
                  height: "100%",
                  overflow: "hidden",
                  p: {
                    xs: 2.5,
                    sm: 3,
                  },
                  borderRadius: "22px",
                  color: "#ffffff",
                  background:
                    "linear-gradient(135deg, #d96f40 0%, #ed8d5d 100%)",
                  boxShadow: "0 16px 38px rgba(217,111,64,0.25)",

                  "&::after": {
                    content: '""',
                    position: "absolute",
                    width: 180,
                    height: 180,
                    right: -70,
                    bottom: -90,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.12)",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      display: "block",
                      mb: 0.5,
                      color: "rgba(255,255,255,0.72)",
                      fontWeight: 850,
                      letterSpacing: "0.08em",
                    }}
                  >
                    SIMPLE SELLING FEES
                  </Typography>

                  <Typography
                    sx={{
                      mb: 0.5,
                      fontSize: {
                        xs: "2.5rem",
                        sm: "3.1rem",
                      },
                      lineHeight: 1,
                      fontWeight: 950,
                      letterSpacing: "-0.04em",
                    }}
                  >
                    {COMMISSION_RATE}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: 850,
                    }}
                  >
                    Commission only when your item sells
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: 430,
                      color: "rgba(255,255,255,0.82)",
                      lineHeight: 1.65,
                    }}
                  >
                    Seller setup is free. FiddlePiddle collects a{" "}
                    {COMMISSION_RATE} commission when a completed item sale
                    occurs.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* FAQs */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="overline"
                sx={{
                  color: BRAND_ORANGE_DARK,
                  fontWeight: 850,
                  letterSpacing: "0.08em",
                }}
              >
                NEED TO KNOW
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  letterSpacing: "-0.025em",
                }}
              >
                Quick seller FAQs
              </Typography>
            </Box>

            {[
              {
                panel: "panel1",
                question: "Why do I have to complete this setup?",
                answer:
                  "Seller setup connects your FiddlePiddle account to Stripe so you can receive payouts securely. It also helps verify sellers and protect both sides of the marketplace.",
              },
              {
                panel: "panel2",
                question: "What information will I need?",
                answer:
                  "You will generally provide basic personal details, your residential address, and your preferred payout destination. Stripe may occasionally request identification or additional documentation.",
              },
              {
                panel: "panel3",
                question: "How long does seller setup take?",
                answer:
                  "Most sellers can submit the required information within a few minutes. If Stripe needs additional verification, approval may take longer.",
              },
              {
                panel: "panel4",
                question: "What does it cost to sell?",
                answer: `Seller setup is free. FiddlePiddle charges a ${COMMISSION_RATE} commission when an item sells.`,
              },
            ].map((faq) => (
              <Accordion
                key={faq.panel}
                expanded={expandedFaq === faq.panel}
                onChange={handleFaqChange(faq.panel)}
                disableGutters
                elevation={0}
                sx={{
                  mb: 1.25,
                  overflow: "hidden",
                  borderRadius: "16px !important",
                  bgcolor: "#ffffff",
                  border: "1px solid #e2e6ea",
                  boxShadow:
                    expandedFaq === faq.panel
                      ? "0 12px 30px rgba(35,45,55,0.07)"
                      : "none",

                  "&::before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 64,
                    px: {
                      xs: 2,
                      sm: 2.5,
                    },

                    "& .MuiAccordionSummary-content": {
                      my: 1.5,
                    },

                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      color: BRAND_ORANGE_DARK,
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    px: {
                      xs: 2,
                      sm: 2.5,
                    },
                    pt: 0,
                    pb: 2.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      maxWidth: 850,
                      lineHeight: 1.7,
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Final CTA */}
          <Paper
            ref={finalCtaRef}
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              scrollMarginTop: "24px",
              p: {
                xs: 2.5,
                sm: 3.5,
                md: 4,
              },
              borderRadius: "26px",
              bgcolor: "#ffffff",
              border: "1px solid #e1e5e9",
              boxShadow: "0 18px 44px rgba(35,45,55,0.08)",

              "&::before": {
                content: '""',
                position: "absolute",
                width: 240,
                height: 240,
                top: -140,
                right: -90,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(231,135,88,0.2), rgba(231,135,88,0) 70%)",
                pointerEvents: "none",
              },
            }}
          >
            <Grid
              container
              spacing={3}
              alignItems="center"
              sx={{
                position: "relative",
                zIndex: 1,
              }}
            >
              <Grid item xs={12} md={6}>
                <Typography
                  variant="overline"
                  sx={{
                    color: BRAND_ORANGE_DARK,
                    fontWeight: 850,
                    letterSpacing: "0.08em",
                  }}
                >
                  READY TO SELL?
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    mt: 0.25,
                    mb: 1,
                    fontWeight: 900,
                    letterSpacing: "-0.035em",
                  }}
                >
                  Set up your seller account.
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    maxWidth: 560,
                    lineHeight: 1.7,
                  }}
                >
                  There is no seller setup fee. FiddlePiddle collects a{" "}
                  {COMMISSION_RATE} commission only when an item sells.
                </Typography>

                <Link
                  component="button"
                  type="button"
                  onClick={handleBrowseInstead}
                  sx={{
                    mt: 1.5,
                    p: 0,
                    minWidth: "auto",
                    color: BRAND_ORANGE_DARK,
                    fontSize: "0.84rem",
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                >
                  Not ready yet? Browse toys instead
                </Link>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: {
                      xs: 2,
                      sm: 2.5,
                    },
                    borderRadius: "20px",
                    bgcolor: "#f7f8fa",
                    border: "1px solid #e5e8eb",
                  }}
                >
                  <FormControlLabel
                    sx={{
                      alignItems: "flex-start",
                      m: 0,
                      mb: 2,

                      "& .MuiFormControlLabel-label": {
                        pt: "2px",
                      },
                    }}
                    control={
                      <Checkbox
                        id="agree-terms"
                        checked={agreedToTerms}
                        onChange={(event) =>
                          setAgreedToTerms(event.target.checked)
                        }
                        sx={{
                          pt: 0,
                          color: "#aab1b7",

                          "&.Mui-checked": {
                            color: BRAND_ORANGE,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          lineHeight: 1.6,
                          userSelect: "none",
                        }}
                      >
                        I agree to FiddlePiddle’s{" "}
                        <Link
                          href="/blog/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: BRAND_ORANGE_DARK,
                            fontWeight: 750,
                          }}
                        >
                          Seller Terms
                        </Link>{" "}
                        and understand that identity and payout verification is
                        required to sell.
                      </Typography>
                    }
                  />

                  <FormActionButton
                    text="Start seller setup"
                    onClick={handleStartSetup}
                    disabled={!agreedToTerms}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.75,
                      mt: 1.5,
                      color: "text.secondary",
                    }}
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: "0.95rem",
                      }}
                    />

                    <Typography variant="caption">
                      Secure setup powered by Stripe
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Presell;
